// ─────────────────────────────────────────────────────────────
// TERRITORIES  (utils-only — components use useTenantConfig().territories)
// ─────────────────────────────────────────────────────────────

export const TERR = [
  { code: 'UK',  name: 'United Kingdom', currency: 'GBP', symbol: '£',   col: null,  flag: '🇬🇧' },
  { code: 'US',  name: 'United States',  currency: 'USD', symbol: '$',   col: 'USD', flag: '🇺🇸' },
  { code: 'EU',  name: 'Europe',         currency: 'EUR', symbol: '€',   col: 'EUR', flag: '🇪🇺' },
  { code: 'AU',  name: 'Australia',      currency: 'AUD', symbol: 'A$',  col: 'AUD', flag: '🇦🇺' },
  { code: 'CA',  name: 'Canada',         currency: 'CAD', symbol: 'C$',  col: 'CAD', flag: '🇨🇦' },
  { code: 'DE',  name: 'Germany',        currency: 'EUR', symbol: '€',   col: 'EUR', flag: '🇩🇪' },
  { code: 'FR',  name: 'France',         currency: 'EUR', symbol: '€',   col: 'EUR', flag: '🇫🇷' },
  { code: 'SE',  name: 'Sweden',         currency: 'SEK', symbol: 'kr',  col: 'SEK', flag: '🇸🇪' },
  { code: 'DK',  name: 'Denmark',        currency: 'DKK', symbol: 'DKK', col: 'DKK', flag: '🇩🇰' },
  { code: 'CH',  name: 'Switzerland',    currency: 'CHF', symbol: 'CHF', col: 'CHF', flag: '🇨🇭' },
  { code: 'STG', name: 'Staging',        currency: 'GBP', symbol: '£',   col: null,  flag: '🏳️'  },
];

// ─────────────────────────────────────────────────────────────
// PRICE LOOKUP TABLE  (GBP → [EUR, USD, AUD, CAD, DKK, SEK, CHF])
// ─────────────────────────────────────────────────────────────

export const DEFAULT_PRICE_TABLE = {
  8:   [10,  12,  15,  10,  75,   100,  15],
  10:  [12,  15,  20,  18,  85,   125,  18],
  12:  [14,  18,  20,  20,  100,  150,  20],
  15:  [18,  22,  30,  25,  125,  200,  25],
  20:  [25,  35,  40,  35,  175,  275,  35],
  25:  [30,  40,  50,  40,  225,  325,  40],
  30:  [35,  45,  60,  45,  275,  400,  45],
  35:  [40,  50,  70,  50,  300,  450,  50],
  40:  [45,  55,  80,  60,  350,  525,  55],
  45:  [50,  60,  90,  70,  375,  600,  60],
  50:  [55,  70,  100, 75,  425,  650,  70],
  55:  [60,  80,  105, 85,  475,  725,  75],
  60:  [65,  85,  115, 95,  525,  800,  80],
  65:  [70,  90,  125, 100, 550,  850,  85],
  70:  [80,  95,  135, 105, 600,  925,  90],
  75:  [85,  100, 145, 115, 650,  1000, 95],
  80:  [90,  110, 155, 120, 700,  1050, 100],
  85:  [95,  115, 165, 130, 750,  1125, 105],
  90:  [100, 125, 175, 140, 775,  1200, 115],
  95:  [110, 130, 185, 150, 800,  1250, 120],
  100: [120, 140, 195, 165, 850,  1300, 130],
  105: [125, 150, 205, 175, 900,  1350, 135],
  110: [130, 160, 215, 185, 950,  1450, 140],
  115: [140, 165, 225, 195, 1000, 1525, 145],
  120: [145, 175, 235, 205, 1050, 1600, 150],
  125: [150, 180, 245, 215, 1100, 1650, 155],
  130: [155, 190, 255, 225, 1150, 1700, 160],
  135: [160, 195, 265, 230, 1200, 1850, 165],
  140: [170, 200, 275, 240, 1250, 1900, 170],
  145: [175, 210, 285, 245, 1300, 1950, 175],
  150: [180, 215, 295, 250, 1350, 2000, 180],
  160: [190, 230, 325, 270, 1400, 2200, 190],
  170: [200, 240, 345, 290, 1450, 2300, 195],
  180: [210, 250, 370, 310, 1550, 2400, 200],
  190: [220, 260, 390, 330, 1650, 2500, 210],
  200: [230, 270, 400, 350, 1700, 2600, 220],
};

export const TABLE_COLS = ['EUR', 'USD', 'AUD', 'CAD', 'DKK', 'SEK', 'CHF'];

// Price table columns for a tenant's territories — keeps the 7 default currencies in their
// original fixed order (existing stored price tables are positionally encoded against it) and
// appends any additional currency a tenant introduces via a custom territory.
export function getTableCols(territories) {
  const extra = [...new Set((territories || []).filter(t => t.col && !TABLE_COLS.includes(t.col)).map(t => t.col))];
  return [...TABLE_COLS, ...extra];
}

// ─────────────────────────────────────────────────────────────
// SIZE LOOKUP TABLES
// ─────────────────────────────────────────────────────────────

// BRA SIZING: UK/US/CA/EU base → [AUS, DE/EU, FR]
export const BRA_SIZE_COLS = ['AUS', 'DE/EU', 'FR'];

// Default territory codes covered by each BRA_SIZE_COLS column — used to translate variant sizes
// on export for tenants who haven't customized the Bra Sizing category's column territories.
export const BRA_SIZE_COL_TERRITORIES = [['AU'], ['DE', 'SE', 'DK', 'CH', 'EU'], ['FR']];

// CLOTHES SIZING: UK base → [US/CA, EU]
export const CLOTHES_SIZE_COLS = ['US/CA', 'EU'];

// Default territory codes covered by each CLOTHES_SIZE_COLS column — used to translate variant
// sizes on export for tenants who haven't customized the Clothes Sizing category's territories.
export const CLOTHES_SIZE_COL_TERRITORIES = [['US', 'CA'], ['EU', 'DE', 'FR', 'SE', 'DK', 'CH']];

// ─────────────────────────────────────────────────────────────
// UTILS-ONLY  (used by pure utility functions, not components)
// Components read these values via useTenantConfig() instead.
// ─────────────────────────────────────────────────────────────

export const DEPT_IDS = ['ops', 'trading', 'creative', 'technical', 'digital', 'studio'];

// Canonical department set — Settings → Departments only toggles which of these are enabled
// per tenant; this is also the fallback used for a tenant that hasn't configured any yet.
export const STANDARD_DEPTS = [
  { id: 'ops',       label: 'Wholesale & Ops',  desc: 'SKUs, barcodes, variants, inventory' },
  { id: 'trading',   label: 'Trading & Buying',  desc: 'Pricing, territories, publish dates' },
  { id: 'creative',  label: 'Creative & Copy',   desc: 'Titles, descriptions' },
  { id: 'technical', label: 'Technical',         desc: 'Fit & fabric, care instructions' },
  { id: 'digital',   label: 'Digital & SEO',     desc: 'Tags, metafields, badges' },
  { id: 'studio',    label: 'Studio',            desc: 'Images, swatches' },
];

// Full pool of available translation languages — used by the tenant admin picker.
export const AVAILABLE_TRANSLATION_LANGS = [
  { code: 'DE', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'FR', label: 'Français',   flag: '🇫🇷' },
  { code: 'SE', label: 'Svenska',    flag: '🇸🇪' },
  { code: 'DK', label: 'Dansk',      flag: '🇩🇰' },
  { code: 'NL', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'CH', label: 'Schweiz',    flag: '🇨🇭' },
  { code: 'ES', label: 'Español',    flag: '🇪🇸' },
  { code: 'IT', label: 'Italiano',   flag: '🇮🇹' },
  { code: 'PT', label: 'Português',  flag: '🇵🇹' },
  { code: 'JA', label: '日本語',     flag: '🇯🇵' },
  { code: 'ZH', label: '中文',       flag: '🇨🇳' },
];

// Default active languages when no tenant config exists (first six of the available pool).
export const TRANSLATION_LANGS = AVAILABLE_TRANSLATION_LANGS.slice(0, 6);

// ─────────────────────────────────────────────────────────────
// SPREADSHEET COLUMN DEFINITIONS
// ─────────────────────────────────────────────────────────────

const FIXED_SHEET_COLS = [
  // Digital & SEO
  { key:'handle',             label:'Handle',             dept:'digital',   width:200, type:'text' },
  { key:'type',               label:'Product Type',       dept:'digital',   width:150, type:'text' },
  { key:'productCategory',    label:'Product Category',   dept:'digital',   width:160, type:'text' },
  { key:'metaColor',          label:'Colour',             dept:'digital',   width:130, type:'text' },
  { key:'tags',               label:'Tags',               dept:'digital',   width:210, type:'tags' },
  // Wholesale & OPS
  { key:'skuBase',            label:'SKU Base',           dept:'ops',       width:130, type:'text' },
  { key:'hsCode',             label:'HS Code',            dept:'ops',       width:115, type:'text' },
  { key:'coo',                label:'COO',                dept:'ops',       width:70,  type:'text' },
  // Trading & Buying
  { key:'basePrice',          label:'Price (£)',          dept:'trading',   width:90,  type:'text' },
  { key:'compareAtPrice',     label:'Compare At (£)',     dept:'trading',   width:110, type:'text' },
  { key:'publishDate',        label:'Publish Date',       dept:'trading',   width:125, type:'date' },
  // Creative & Copy
  { key:'title',              label:'Full Title',         dept:'creative',  width:240, type:'text' },
  { key:'bodyHtml',           label:'Body HTML',          dept:'creative',  width:110, type:'longtext' },
  // Technical
  { key:'fitFabric',          label:'Fit & Fabric',       dept:'technical', width:115, type:'longtext' },
  { key:'careInstructions',   label:'Care Instructions',  dept:'technical', width:130, type:'longtext' },
  { key:'fabricWeight',       label:'Fabric Weight',      dept:'technical', width:110, type:'text' },
  // Studio
  { key:'imageUrl',           label:'Image URL',          dept:'studio',    width:110, type:'text' },
];

// Splices tenant-configured custom fields into their assigned department's column group (each
// field picks its own dept in Settings → Fields; unset defaults to 'digital' for backward
// compatibility). Also drops any field a tenant has deleted out (Settings → Fields).
export function getSheetCols(tenantConfig) {
  const disabled = tenantConfig?.disabledFields || [];
  const fieldLabels = tenantConfig?.fieldLabels || {};
  const metaCols = (tenantConfig?.metafields || []).map(f => ({
    key: f.key,
    label: f.label,
    dept: f.dept || 'digital',
    width: 150,
    type: f.type === 'multi_line_text_field' || f.type === 'list.single_line_text_field' || f.type === 'longtext' ? 'longtext' : 'text',
  }));
  // Built-in department order, then any extra departments a tenant's metafields are assigned to
  // (custom sections) — so custom-section fields still get a spreadsheet column group.
  const deptOrder = [...new Set([...FIXED_SHEET_COLS.map(c => c.dept), ...metaCols.map(m => m.dept)])];
  const cols = [];
  for (const dept of deptOrder) {
    cols.push(...FIXED_SHEET_COLS
      .filter(c => c.dept === dept && !disabled.includes(c.key))
      .map(c => fieldLabels[c.key] ? { ...c, label: fieldLabels[c.key] } : c));
    cols.push(...metaCols.filter(m => m.dept === dept));
  }
  return cols;
}

export const SHEET_DEPT_META = {
  ops:       { label:'Wholesale & Ops',  hdr:'#e8e8e8', bg:'#fafafa', fg:'#555555' },
  trading:   { label:'Trading & Buying', hdr:'#fde68a', bg:'#fffdf5', fg:'#92400e' },
  creative:  { label:'Creative & Copy',  hdr:'#bfdbfe', bg:'#f8fbff', fg:'#1e40af' },
  technical: { label:'Technical',        hdr:'#bbf7d0', bg:'#f7fdf9', fg:'#065f46' },
  digital:   { label:'Digital & SEO',    hdr:'#e9d5ff', bg:'#fdf8ff', fg:'#6b21a8' },
  studio:    { label:'Studio',           bg:'#fff9f5',  hdr:'#fed7aa', fg:'#9a3412' },
};

// ─────────────────────────────────────────────────────────────
// STORAGE KEY
// ─────────────────────────────────────────────────────────────

// localStorage namespace for the offline fallback cache (suffixed per-tenant in useAppData).
// Supabase is the primary store, so bumping this name only clears the local fallback, which
// self-heals on the next successful load — no tenant data lives solely here.
export const SK = 'range-builder-v4';
